import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, ShoppingCart, Eye, X, Check, ChevronsUpDown } from 'lucide-react';

const Purchases = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorOpen, setVendorOpen] = useState(false);
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  
  // Main dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [purchaseDetails, setPurchaseDetails] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Detail popup state
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [detailFormData, setDetailFormData] = useState({});
  const [editingDetailIndex, setEditingDetailIndex] = useState(null);
  
  // View dialog
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingPurchase, setViewingPurchase] = useState(null);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'RM_STORE']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [purchasesRes, vendorsRes, employeesRes, itemsRes, uomsRes] = await Promise.all([
        api.get('/purchases'),
        api.get('/vendors'),
        api.get('/employees'),
        api.get('/items'),
        api.get('/uoms')
      ]);
      setData(purchasesRes.data);
      setVendors(vendorsRes.data);
      setEmployees(employeesRes.data);
      setItems(itemsRes.data);
      setUoms(uomsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? `${vendor.code} - ${vendor.name}` : `ID: ${vendorId}`;
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return '-';
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.employee_code} - ${emp.first_name} ${emp.last_name || ''}` : `ID: ${employeeId}`;
  };

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? `${item.code} - ${item.name}` : `ID: ${itemId}`;
  };

  const getUomName = (uomId) => {
    if (!uomId) return '-';
    const uom = uoms.find(u => u.id === uomId);
    return uom ? uom.code : '-';
  };

  const filteredVendors = vendors.filter(v => v.is_active);
  const filteredEmployees = employees.filter(e => e.is_active);

  const filteredItems = items.filter(i => {
    if (!itemSearch) return i.is_active;
    const term = itemSearch.toLowerCase();
    return i.is_active && (i.code.toLowerCase().includes(term) || i.name.toLowerCase().includes(term));
  });

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        purchase_date: item.purchase_date || '',
        vendor_id: (item.vendor_id ? item.vendor_id.toString() : ''),
        invoice_no: item.invoice_no || '',
        status: item.status || 'WAITING_FOR_INSPECTION',
        store_id: '8', // Fixed store ID
        invoice_total: item.invoice_total || '',
        employee_id: (item.employee_id ? item.employee_id.toString() : ''),
        remarks: item.remarks || '',
      });
      setPurchaseDetails(item.details || []);
    } else {
      setEditingItem(null);
      setFormData({
        purchase_date: new Date().toISOString().split('T')[0],
        vendor_id: '',
        invoice_no: '',
        status: 'WAITING_FOR_INSPECTION',
        store_id: '8', // Fixed store ID
        invoice_total: '',
        employee_id: '',
        remarks: '',
      });
      setPurchaseDetails([]);
    }
    setVendorOpen(false);
    setEmployeeOpen(false);
    setIsDialogOpen(true);
  };

  const handleOpenDetailDialog = (detail = null, index = null) => {
    if (detail) {
      setDetailFormData({
        item_id: (detail.item_id ? detail.item_id.toString() : ''),
        quantity: detail.quantity || '',
        uom_id: (detail.uom_id ? detail.uom_id.toString() : ''),
        tax_percent: detail.tax_percent || '',
        unit_rate: detail.unit_rate || '',
        total_rate: detail.total_rate || '',
        batch_no: detail.batch_no || '',
      });
      setEditingDetailIndex(index);
    } else {
      setDetailFormData({
        item_id: '',
        quantity: '',
        uom_id: '',
        tax_percent: '',
        unit_rate: '',
        total_rate: '',
        batch_no: '',
      });
      setEditingDetailIndex(null);
    }
    setItemSearch('');
    setIsDetailDialogOpen(true);
  };

  const calculateTotalRate = (quantity, unitRate, taxPercent) => {
    if (quantity && unitRate) {
      var base = parseFloat(quantity) * parseFloat(unitRate);
      var tax = taxPercent ? base * parseFloat(taxPercent) / 100 : 0;
      return (base + tax).toFixed(4);
    }
    return '';
  };

  const handleDetailQuantityChange = (value) => {
    var newTotal = calculateTotalRate(value, detailFormData.unit_rate, detailFormData.tax_percent);
    setDetailFormData({
      ...detailFormData,
      quantity: value,
      total_rate: newTotal
    });
  };

  const handleDetailUnitRateChange = (value) => {
    var newTotal = calculateTotalRate(detailFormData.quantity, value, detailFormData.tax_percent);
    setDetailFormData({
      ...detailFormData,
      unit_rate: value,
      total_rate: newTotal
    });
  };

  const handleDetailTaxChange = (value) => {
    var newTotal = calculateTotalRate(detailFormData.quantity, detailFormData.unit_rate, value);
    setDetailFormData({
      ...detailFormData,
      tax_percent: value,
      total_rate: newTotal
    });
  };

  const handleDetailItemChange = (itemId) => {
    const selectedItem = items.find(function(i) { return i.id.toString() === itemId; });
    setDetailFormData(function(prev) {
      return Object.assign({}, prev, {
        item_id: itemId,
        uom_id: (selectedItem && selectedItem.uom_id) ? selectedItem.uom_id.toString() : prev.uom_id,
      });
    });
    // Fetch last purchase rate for this item
    api.get('/items/' + itemId + '/last-purchase-rate').then(function(res) {
      var d = res.data;
      if (d.unit_rate !== null || d.tax_percent !== null) {
        setDetailFormData(function(prev) {
          var rate = d.unit_rate !== null ? d.unit_rate.toString() : prev.unit_rate;
          var tax = d.tax_percent !== null ? d.tax_percent.toString() : prev.tax_percent;
          var newTotal = calculateTotalRate(prev.quantity, rate, tax);
          return Object.assign({}, prev, { unit_rate: rate, tax_percent: tax, total_rate: newTotal });
        });
      }
    }).catch(function() { /* ignore */ });
  };

  const handleAddDetail = () => {
    if (!detailFormData.item_id || !detailFormData.quantity) {
      toast.error('Item and Quantity are required');
      return;
    }

    const newDetail = {
      item_id: parseInt(detailFormData.item_id),
      quantity: parseFloat(detailFormData.quantity),
      uom_id: detailFormData.uom_id ? parseInt(detailFormData.uom_id) : null,
      tax_percent: detailFormData.tax_percent ? parseFloat(detailFormData.tax_percent) : null,
      unit_rate: detailFormData.unit_rate ? parseFloat(detailFormData.unit_rate) : null,
      total_rate: detailFormData.total_rate ? parseFloat(detailFormData.total_rate) : null,
      batch_no: detailFormData.batch_no || null,
    };

    if (editingDetailIndex !== null) {
      const updated = [...purchaseDetails];
      updated[editingDetailIndex] = newDetail;
      setPurchaseDetails(updated);
    } else {
      setPurchaseDetails([...purchaseDetails, newDetail]);
    }

    // Recalculate invoice total
    const allDetails = editingDetailIndex !== null 
      ? [...purchaseDetails.slice(0, editingDetailIndex), newDetail, ...purchaseDetails.slice(editingDetailIndex + 1)]
      : [...purchaseDetails, newDetail];
    const total = allDetails.reduce((sum, d) => sum + (parseFloat(d.total_rate) || 0), 0);
    setFormData(prev => ({ ...prev, invoice_total: total.toFixed(2) }));

    setIsDetailDialogOpen(false);
  };

  const handleRemoveDetail = (index) => {
    const updated = purchaseDetails.filter((_, i) => i !== index);
    setPurchaseDetails(updated);
    // Recalculate total
    const total = updated.reduce((sum, d) => sum + (parseFloat(d.total_rate) || 0), 0);
    setFormData(prev => ({ ...prev, invoice_total: total.toFixed(2) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vendor_id || !formData.purchase_date || !formData.invoice_no) {
      toast.error('Vendor, Invoice Date and Invoice No are required');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        purchase_date: formData.purchase_date,
        vendor_id: parseInt(formData.vendor_id),
        invoice_no: formData.invoice_no || null,
        status: formData.status,
        store_id: parseInt(formData.store_id),
        invoice_total: formData.invoice_total ? parseFloat(formData.invoice_total) : null,
        employee_id: formData.employee_id ? parseInt(formData.employee_id) : null,
        remarks: formData.remarks || null,
        details: purchaseDetails,
      };

      if (editingItem) {
        await api.put(`/purchases/${editingItem.id}`, submitData);
        // Update details separately if needed
        toast.success('Purchase updated');
      } else {
        await api.post('/purchases', submitData);
        toast.success('Purchase created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this purchase?')) return;
    try {
      await api.delete(`/purchases/${item.id}`);
      toast.success('Purchase deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const handleView = async (item) => {
    try {
      const response = await api.get(`/purchases/${item.id}`);
      setViewingPurchase(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load purchase details');
    }
  };

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const vendorName = getVendorName(item.vendor_id).toLowerCase();
    return (
      vendorName.includes(term) ||
      (item.grn_no ? item.grn_no.toString() : '').includes(term) ||
      (item.invoice_no || '').toLowerCase().includes(term)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'WAITING_FOR_INSPECTION':
        return <span className="badge-warning">Waiting for Inspection</span>;
      case 'INSPECTED':
        return <span className="badge-success">Inspected</span>;
      case 'REJECTED':
        return <span className="badge-danger">Rejected</span>;
      default:
        return <span className="badge-info">{status}</span>;
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="purchases">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-sky-500" />
            Purchases / GRN
          </h1>
          <p className="text-slate-500 text-sm">{data.length} records</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Purchase
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by GRN, Invoice, Vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No purchases found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>GRN No</TableHead>
                    <TableHead>Invoice Date</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Invoice No</TableHead>
                    <TableHead>Invoice Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono font-bold">{item.grn_no}</TableCell>
                      <TableCell>{new Date(item.purchase_date).toLocaleDateString()}</TableCell>
                      <TableCell className="font-medium">{getVendorName(item.vendor_id)}</TableCell>
                      <TableCell>{item.invoice_no || '-'}</TableCell>
                      <TableCell className="font-mono">{item.invoice_total ? `₹${item.invoice_total}` : '-'}</TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleView(item)} className="h-8 w-8">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Purchase Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? `Edit Purchase - GRN #${editingItem.grn_no}` : 'New Purchase'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
              <div>
                <Label className="form-label">Invoice Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Vendor <span className="text-red-500">*</span></Label>
                <Popover open={vendorOpen} onOpenChange={setVendorOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={vendorOpen}
                      className="w-full justify-between rounded-sm font-normal text-sm h-9"
                    >
                      <span className="truncate">
                        {formData.vendor_id
                          ? (() => { const v = vendors.find(v => v.id.toString() === formData.vendor_id); return v ? `${v.code} - ${v.name}` : 'Select Vendor'; })()
                          : 'Select Vendor'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                    <Command>
                      <CommandInput placeholder="Search vendor..." />
                      <CommandList>
                        <CommandEmpty>No vendors found.</CommandEmpty>
                        <CommandGroup>
                          {filteredVendors.map((vendor) => (
                            <CommandItem
                              key={vendor.id}
                              value={`${vendor.code} - ${vendor.name}`}
                              onSelect={() => {
                                setFormData(prev => ({ ...prev, vendor_id: vendor.id.toString() }));
                                setVendorOpen(false);
                              }}
                            >
                              <Check className={formData.vendor_id === vendor.id.toString() ? 'mr-2 h-4 w-4 opacity-100' : 'mr-2 h-4 w-4 opacity-0'} />
                              {vendor.code} - {vendor.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="form-label">Invoice No <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.invoice_no}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoice_no: e.target.value }))}
                  placeholder="INV-001"
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">GRN</Label>
                <Input
                  value={editingItem ? editingItem.grn_no : 'Auto-generated on save'}
                  disabled
                  className="rounded-sm bg-slate-100"
                />
              </div>
              <div>
                <Label className="form-label">Status</Label>
                <Input
                  value="Waiting for Inspection"
                  disabled
                  className="rounded-sm bg-slate-100"
                />
              </div>
              <div>
                <Label className="form-label">Invoice Total <span className="text-xs text-slate-400">auto</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.invoice_total}
                  readOnly
                  placeholder="0.00"
                  className="rounded-sm bg-slate-50"
                />
              </div>
              <div>
                <Label className="form-label">Received By</Label>
                <Popover open={employeeOpen} onOpenChange={setEmployeeOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={employeeOpen}
                      className="w-full justify-between rounded-sm font-normal text-sm h-9"
                    >
                      <span className="truncate">
                        {formData.employee_id
                          ? (() => { const e = employees.find(e => e.id.toString() === formData.employee_id); return e ? `${e.employee_code} - ${e.first_name} ${e.last_name || ''}` : 'Select Received By'; })()
                          : 'Select Received By'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                    <Command>
                      <CommandInput placeholder="Search employee..." />
                      <CommandList>
                        <CommandEmpty>No employees found.</CommandEmpty>
                        <CommandGroup>
                          {filteredEmployees.map((emp) => (
                            <CommandItem
                              key={emp.id}
                              value={`${emp.employee_code} - ${emp.first_name} ${emp.last_name || ''}`}
                              onSelect={() => {
                                setFormData(prev => ({ ...prev, employee_id: emp.id.toString() }));
                                setEmployeeOpen(false);
                              }}
                            >
                              <Check className={formData.employee_id === emp.id.toString() ? 'mr-2 h-4 w-4 opacity-100' : 'mr-2 h-4 w-4 opacity-0'} />
                              {emp.employee_code} - {emp.first_name} {emp.last_name || ''}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Purchase Details Section */}
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Purchase Details</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenDetailDialog()}
                  className="rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>
              
              {purchaseDetails.length === 0 ? (
                <div className="text-center py-6 text-slate-500 border border-dashed rounded-sm">
                  No items added. Click "Add Item" to add purchase details.
                </div>
              ) : (
                <div className="border rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Item</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>UOM</TableHead>
                        <TableHead>Batch No</TableHead>
                        <TableHead>Tax %</TableHead>
                        <TableHead>Unit Rate</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {purchaseDetails.map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-sm">{getItemName(detail.item_id)}</TableCell>
                          <TableCell className="font-mono">{detail.quantity}</TableCell>
                          <TableCell>{getUomName(detail.uom_id)}</TableCell>
                          <TableCell className="text-sm">{detail.batch_no || '-'}</TableCell>
                          <TableCell className="font-mono">{detail.tax_percent || '-'}</TableCell>
                          <TableCell className="font-mono">{detail.unit_rate || '-'}</TableCell>
                          <TableCell className="font-mono font-medium">{detail.total_rate || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDetailDialog(detail, index)}
                                className="h-7 w-7"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveDetail(index)}
                                className="h-7 w-7 text-red-600"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Detail Popup */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDetailIndex !== null ? 'Edit Item' : 'Add Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="form-label">Item <span className="text-red-500">*</span></Label>
              <div className="space-y-1">
                <Input
                  placeholder="Search item..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  className="rounded-sm text-sm"
                />
                <Select
                  value={detailFormData.item_id}
                  onValueChange={handleDetailItemChange}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Item" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {filteredItems.slice(0, 50).map((item) => (
                      <SelectItem key={item.id} value={item.id.toString()}>
                        {item.code} - {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Quantity <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={detailFormData.quantity}
                  onChange={(e) => handleDetailQuantityChange(e.target.value)}
                  placeholder="0"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">UOM</Label>
                <Select
                  value={detailFormData.uom_id}
                  onValueChange={(val) => setDetailFormData({ ...detailFormData, uom_id: val })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select UOM" />
                  </SelectTrigger>
                  <SelectContent>
                    {uoms.filter(u => u.is_active).map((uom) => (
                      <SelectItem key={uom.id} value={uom.id.toString()}>
                        {uom.code} - {uom.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="form-label">Batch No</Label>
              <Input
                value={detailFormData.batch_no}
                onChange={(e) => setDetailFormData({ ...detailFormData, batch_no: e.target.value })}
                placeholder="Batch No"
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="form-label">Tax %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={detailFormData.tax_percent}
                  onChange={function(e) { handleDetailTaxChange(e.target.value); }}
                  placeholder="18"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Unit Rate</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={detailFormData.unit_rate}
                  onChange={function(e) { handleDetailUnitRateChange(e.target.value); }}
                  placeholder="0.00"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Total Rate <span className="text-xs text-slate-400">auto</span></Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={detailFormData.total_rate}
                  readOnly
                  placeholder="0.00"
                  className="rounded-sm bg-slate-50"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsDetailDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button type="button" onClick={handleAddDetail} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
              {editingDetailIndex !== null ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Purchase Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Details - GRN #{(viewingPurchase && viewingPurchase.grn_no)}</DialogTitle>
          </DialogHeader>
          {viewingPurchase && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-slate-500">GRN No</span>
                  <p className="font-medium">{viewingPurchase.grn_no}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Invoice Date</span>
                  <p className="font-medium">{new Date(viewingPurchase.purchase_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Vendor</span>
                  <p className="font-medium">{getVendorName(viewingPurchase.vendor_id)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Invoice No</span>
                  <p className="font-medium">{viewingPurchase.invoice_no || '-'}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Status</span>
                  <p>{getStatusBadge(viewingPurchase.status)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Invoice Total</span>
                  <p className="font-medium font-mono">₹{viewingPurchase.invoice_total || 0}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Received By</span>
                  <p className="font-medium">{getEmployeeName(viewingPurchase.employee_id)}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Line Items ({(viewingPurchase.details ? viewingPurchase.details.length : 0) || 0})</h4>
                {(viewingPurchase.details ? viewingPurchase.details.length : 0) > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Item</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>UOM</TableHead>
                        <TableHead>Batch No</TableHead>
                        <TableHead>Tax %</TableHead>
                        <TableHead>Unit Rate</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingPurchase.details.map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell>{getItemName(detail.item_id)}</TableCell>
                          <TableCell className="font-mono">{detail.quantity}</TableCell>
                          <TableCell>{getUomName(detail.uom_id)}</TableCell>
                          <TableCell className="text-sm">{detail.batch_no || '-'}</TableCell>
                          <TableCell className="font-mono">{detail.tax_percent || '-'}</TableCell>
                          <TableCell className="font-mono">{detail.unit_rate || '-'}</TableCell>
                          <TableCell className="font-mono font-medium">{detail.total_rate || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-slate-500">No line items</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Purchases;
